import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ApiMessageResponse } from "@core/types/apiMessageResponse";
import type { OriginScope } from "@features/report/template/types/reportTemplate";

export type DepartmentReportDocument = {
  department_report_document_id: number;
  department_id: number;
  origin_scope: OriginScope;
  name: string;
  description?: string | null;
  is_required: boolean;
  is_active: boolean;
  required_at?: string | null;
  list_order: number;
};

export type DepartmentReportDocumentQueryResponse = {
  department_id: number;
  locked: boolean;
  locked_reason: string | null;
  data: DepartmentReportDocument[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from?: number | null;
  to?: number | null;
};

export type DepartmentReportDocumentQueryParams = {
  page?: number;
  perPage?: number;
  all?: boolean;
  freetext?: string;
  name?: string;
  description?: string;
  departmentReportDocumentId?: number;
  originScope?: OriginScope | string;
  isActive?: boolean;
};

export type UpsertDepartmentReportDocumentPayload = {
  name: string;
  description?: string | null;
  is_required: boolean;
  required_at?: string | null;
  list_order: number;
  origin_scope?: OriginScope;
};

function toQueryString(params: DepartmentReportDocumentQueryParams): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export async function queryDepartmentReportDocuments(
  nationId: number,
  departmentId: number,
  params: DepartmentReportDocumentQueryParams = { all: true }
): Promise<ApiResponse<DepartmentReportDocumentQueryResponse>> {
  return sendRequest<unknown, DepartmentReportDocumentQueryResponse>(
    "GET",
    `/nation/${nationId}/department/${departmentId}/report-document/query${toQueryString(params)}`
  );
}

export async function createDepartmentReportDocument(
  nationId: number,
  departmentId: number,
  payload: UpsertDepartmentReportDocumentPayload
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<UpsertDepartmentReportDocumentPayload, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/create`,
    payload
  );
}

export async function updateDepartmentReportDocument(
  nationId: number,
  departmentId: number,
  id: number,
  payload: UpsertDepartmentReportDocumentPayload
): Promise<ApiResponse<ApiMessageResponse>> {
  const { origin_scope: _omit, ...body } = payload;
  return sendRequest<Omit<UpsertDepartmentReportDocumentPayload, "origin_scope">, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/${id}/update`,
    body
  );
}

export async function removeDepartmentReportDocument(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/${id}/remove`
  );
}

export async function enableDepartmentReportDocument(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/${id}/enable`
  );
}

export async function disableDepartmentReportDocument(
  nationId: number,
  departmentId: number,
  id: number
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<unknown, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/${id}/disable`
  );
}

export async function changeDepartmentReportDocumentOrigin(
  nationId: number,
  departmentId: number,
  id: number,
  new_origin_scope: OriginScope
): Promise<ApiResponse<ApiMessageResponse>> {
  return sendRequest<{ new_origin_scope: OriginScope }, ApiMessageResponse>(
    "POST",
    `/nation/${nationId}/department/${departmentId}/report-document/${id}/changeorigin`,
    { new_origin_scope }
  );
}
